import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { ExploreContent as FallbackExploreContent } from '../pages/explore/data';
import { withTourSlug, withTourSlugs } from '../utils/tourUrls';
import { optimizeMediaUrl } from '../utils/media';

const TourContext = createContext({
  tours: withTourSlugs(FallbackExploreContent),
  loading: true,
  error: null,
  refreshTours: () => Promise.resolve()
});

const getTourList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.tours)) return payload.tours;
  return [];
};

const fallbackTours = withTourSlugs(FallbackExploreContent);
const BLUEPRINT_REVIEWED_AT = Date.parse('2026-07-17T00:00:00Z');

const optimizeTourMedia = (value) => {
  if (typeof value === 'string') return optimizeMediaUrl(value);
  if (Array.isArray(value)) return value.map(optimizeTourMedia);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, nestedValue]) => [key, optimizeTourMedia(nestedValue)]),
    );
  }
  return value;
};

const mergeWithDisplayFallback = (apiTours) => apiTours.map((tour) => {
  const fallback = FallbackExploreContent.find((item) => Number(item.id) === Number(tour.id));
  if (!fallback) return optimizeTourMedia(withTourSlug(tour));

  const apiReviewTime = Date.parse(tour.lastReviewedAt || tour.updatedAt || '');
  const hasReviewedApiOptions = Number.isFinite(apiReviewTime)
    && apiReviewTime >= BLUEPRINT_REVIEWED_AT
    && tour.options?.length;

  return optimizeTourMedia(withTourSlug({
    ...fallback,
    ...tour,
    description: tour.description ?? fallback.description,
    content: tour.content ?? fallback.content,
    package: tour.package?.length ? tour.package : fallback.package,
    options: tour.options ?? fallback.options,
    brochure: tour.brochure || tour.brochureUrl || fallback.brochure
  }));
});

export const useTours = () => useContext(TourContext);

export const TourProvider = ({ children }) => {
  const [tours, setTours] = useState(fallbackTours);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTours = async (signal) => {
    const apiUrl = import.meta.env.VITE_API_URL || '/api';
    const response = await fetch(`${apiUrl}/explore-content`, {
      signal,
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) throw new Error(`Tour API returned ${response.status}`);
    const payload = await response.json();
    const apiTours = getTourList(payload);
    setTours(mergeWithDisplayFallback(apiTours.filter(tour => (tour.indexStatus || 'published') !== 'draft')));
    setError(null);
  };

  const refreshTours = async () => {
    setLoading(true);
    try {
      await fetchTours();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchTours(controller.signal)
      .catch((requestError) => {
        if (requestError.name !== 'AbortError') setError(requestError.message);
      })
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, []);

  const value = useMemo(
    () => ({ tours, loading, error, refreshTours }),
    [tours, loading, error]
  );

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
};

TourProvider.propTypes = { children: PropTypes.node.isRequired };
